import { ChangeDetectionStrategy, Component, Input, OnDestroy, signal, TemplateRef, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { combineLatest, map, Observable, of, switchMap } from "rxjs";
import { CreateInstancePopupComponent } from "../create-instance-popup/create-instance-popup.component";
import { DemoPortfolioService, filterColors, FilterValue } from "./demo-portfolio.service";
import { ValueChangedEvent } from "devextreme/ui/text_box";
import { DemoTileViewModel, SuggestedDemo, SuggestedBehavior } from "../../core/model/ContentManagement";
import { CollectionService } from "../../services/collection.service";
import { DemoNewsService } from "../../services/demo-news.service";
import { TemplateDialogData, TemplateDialogComponent } from "../../shared/template-dialog/template-dialog.component";
import { SelectionChangedEvent } from "devextreme/ui/accordion";
import { DxAccordionComponent } from "devextreme-angular";

@Component({
  selector: "brd-demo-portfolio",
  templateUrl: "./demo-portfolio.component.html",
  styleUrl: "./demo-portfolio.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DemoPortfolioComponent implements OnDestroy {
  @ViewChild("completeDialog", { read: TemplateRef })
  completeDialogTemplate!: TemplateRef<TemplateDialogData>;
  @Input() defaultGroup!: boolean;
  @Input() instancesQuotaExceeded!: boolean;
  @ViewChild(DxAccordionComponent) accordionGroups!: DxAccordionComponent;

  creationType: "custom" | "preset" = "custom";

  totalCapacity = {
    maxRamConsumption: 0,
    maxDiskConsumption: 0
  };
  remainingCapacity = {
    ramConsumption: 0,
    diskConsumption: 0
  };

  loadingAssets = false;
  loadingTemplates = false;

  selectedDemos: DemoTileViewModel[] = [];
  demoAssets: DemoTileViewModel[] = [];
  collectionIds$: Observable<number[]> = of([]);
  suggestedDemos: SuggestedDemo[] = [];
  expandedGroups: number[] = [];
  areAllGroupsSelected = signal(true);

  expandFilters = false;

  protected searchButtonOptions = {
    icon: "search",
    stylingMode: "text",
    type: "noraml",
    hoverStateEnabled: false,
    activeStateEnabled: false,
    elementAttr: {
      class: "search-icon"
    }
  };

  filterColors = filterColors;
  filterMacrosectors$ = combineLatest([this.demoPortfolioService.macrosectors$, this.demoPortfolioService.selectedMacrosectors$]).pipe(
    map(([macrosectors, selectedMacrosectors]) => ({ macrosectors, selectedMacrosectors: selectedMacrosectors.map(x => x.id) }))
  );
  filterDomains$ = combineLatest([this.demoPortfolioService.domains$, this.demoPortfolioService.selectedDomains$]).pipe(
    map(([domains, selectedDomains]) => ({ domains, selectedDomains: selectedDomains.map(x => x.id) }))
  );
  filterProcesses$ = combineLatest([this.demoPortfolioService.process$, this.demoPortfolioService.selectedProcess$]).pipe(
    map(([processes, selectedProcesses]) => ({ processes, selectedProcesses: selectedProcesses.map(x => x.id) }))
  );
  filterUseCases$ = combineLatest([this.demoPortfolioService.useCases$, this.demoPortfolioService.selectedUseCases$]).pipe(
    map(([useCases, selectedUseCases]) => ({ useCases, selectedUseCases: selectedUseCases.map(x => x.id) }))
  );
  filterFunctionalities$ = combineLatest([
    this.demoPortfolioService.functionalities$,
    this.demoPortfolioService.selectedFunctionalities$
  ]).pipe(
    map(([functionalities, selectedFunctionalities]) => ({
      functionalities,
      selectedFunctionalities: selectedFunctionalities.map(x => x.id)
    }))
  );
  filterIndustries$ = combineLatest([this.demoPortfolioService.industries$, this.demoPortfolioService.selectedIndustries$]).pipe(
    map(([industries, selectedIndustries]) => ({ industries, selectedIndustries: selectedIndustries.map(x => x.id) }))
  );
  filterCategories$ = combineLatest([this.demoPortfolioService.categories$, this.demoPortfolioService.selectedCategories$]).pipe(
    map(([categories, selectedCategories]) => ({ categories, selectedCategories: selectedCategories.map(x => x.id) }))
  );

  filters$ = this.demoPortfolioService.filters$;
  demoAssets$ = combineLatest([this.demoPortfolioService.demoAssets$, this.demoNewsService.newsToRead$]).pipe(
    map(([groupAssets, newsToRead]) => {
      return groupAssets
        .map(group => {
          group.assets = group.assets.map(demo => {
            const news = newsToRead.find(x => x.relatedAssetIds.includes(demo.id));
            if (news && news.isCategoryKeyword && !demo.default) {
              demo.newsCategoryName = news.categoryName;
              demo.newsCategoryColor = news.categoryColor;
            } else {
              demo.newsCategoryName = undefined;
              demo.newsCategoryColor = undefined;
            }
            return demo;
          });

          return group;
        })
        .sort((a, b) => {
          if (!a.assets.length && b.assets.length) return 1;
          else if (a.assets.length && !b.assets.length) return -1;
          else return a.categoryOrder - b.categoryOrder;
        });
    })
  );
  demoTemplates$ = this.demoPortfolioService.demoTemplates$;

  constructor(
    private collectionService: CollectionService,
    private demoPortfolioService: DemoPortfolioService,
    private readonly dialog: MatDialog,
    private readonly demoNewsService: DemoNewsService
  ) {
    this.loadingAssets = true;
    this.demoPortfolioService
      .initFilters()
      .pipe(
        takeUntilDestroyed(),
        switchMap(res => {
          this.totalCapacity = res.limits;
          this.remainingCapacity = {
            ramConsumption: res.limits.maxRamConsumption,
            diskConsumption: res.limits.maxDiskConsumption
          };

          return this.demoPortfolioService.initDemoAssets();
        }),
        map(assetGroups => {
          this.demoAssets = [...assetGroups.flatMap(x => x.assets)];
          this.expandedGroups = assetGroups.map(x => x.categoryId);
          this.loadingAssets = false;
        })
      )
      .subscribe();

    this.loadingTemplates = true;
    this.demoPortfolioService
      .initDemoTemplates()
      .pipe(
        takeUntilDestroyed(),
        map(() => {
          this.loadingTemplates = false;
        })
      )
      .subscribe();

    this.collectionIds$ = this.collectionService.collection$.pipe(
      takeUntilDestroyed(),
      map(collection => {
        const collectionIds = collection?.map(x => x.id) ?? [];

        let newSuggestedDemos: SuggestedDemo[] = [];
        collection.forEach(x => {
          newSuggestedDemos = [
            ...newSuggestedDemos,
            ...x.suggestedDemos.filter(s => newSuggestedDemos.every(nx => nx.suggestedId !== s.suggestedId))
          ];
        });
        this.suggestedDemos = newSuggestedDemos;

        this.remainingCapacity.ramConsumption =
          this.totalCapacity.maxRamConsumption -
          collection.reduce(
            (p, c) =>
              p +
              (c.ramConsumption +
                c.suggestedDemos
                  .filter(x => x.behavior === SuggestedBehavior.default)
                  .reduce((acc, s) => acc + s.suggestedRamConsumption, 0)),
            0
          );
        this.remainingCapacity.diskConsumption =
          this.totalCapacity.maxDiskConsumption -
          collection.reduce(
            (p, c) =>
              p +
              (c.diskConsumption +
                c.suggestedDemos
                  .filter(x => x.behavior === SuggestedBehavior.default)
                  .reduce((acc, s) => acc + s.suggestedDiskConsumption, 0)),
            0
          );

        this.selectedDemos = collection;

        return collectionIds;
      })
    );
  }

  ngOnDestroy(): void {
    this.collectionService.resetCollection();
  }

  setCreationType(type: "custom" | "preset"): void {
    this.creationType = type;
  }

  createInstanceClick(event: Event): void {
    event.stopPropagation();

    let selectedDemos = this.selectedDemos.map(x => ({
      demoId: x.id,
      name: x.name,
      isDefault: false
    }));
    selectedDemos = selectedDemos.concat(
      this.demoAssets
        .filter(
          x =>
            this.suggestedDemos.some(s => s.suggestedId === x.id && s.behavior === SuggestedBehavior.default) &&
            selectedDemos.every(d => d.demoId !== x.id)
        )
        .map(x => ({ demoId: x.id, name: `Default - ${x.name}`, isDefault: false }))
    );
    selectedDemos = selectedDemos.concat(
      this.demoAssets
        .filter(x => x.default && selectedDemos.every(d => d.demoId !== x.id))
        .map(x => ({ demoId: x.id, name: `Default - ${x.name}`, isDefault: true }))
    );

    const dialogRef = this.dialog.open(CreateInstancePopupComponent, {
      width: "1310px",
      height: "770px",
      data: {
        selectedDemos
      }
    });

    dialogRef
      .afterClosed()
      .pipe(
        map(res => {
          if (res) this.collectionService.resetCollection();
          return res;
        }),
        switchMap(res => {
          if (!res) return of(res);
          const completeDialogRef = this.dialog.open(TemplateDialogComponent, {
            width: "512px",
            height: "224px",
            data: {
              title: "Demo Environment going to be created!",
              templateVariable: this.completeDialogTemplate
            }
          });
          return completeDialogRef.afterClosed();
        })
      )
      .subscribe();
  }

  updateFilter({ value, action }: { value: FilterValue; action: "ADD" | "REMOVE" }): void {
    switch (action) {
      case "ADD":
        this.demoPortfolioService.addFilter(value);
        break;
      case "REMOVE":
        this.demoPortfolioService.removeFilter(value);
        break;
    }
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  searchChange(event: ValueChangedEvent): void {
    this.demoPortfolioService.updateSearch(event.value);
  }

  groupSelectionChanged(event: SelectionChangedEvent): void {
    this.areAllGroupsSelected.set(this.expandedGroups.length === event.component.option("selectedItemKeys")?.length);
  }

  toggleExpandedGroup(): void {
    this.accordionGroups.instance.option("selectedItemKeys", this.areAllGroupsSelected() ? [] : this.expandedGroups);
  }
}
